import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@hooks/useAuth';
import { usePendingReviewCount } from '@hooks/usePendingReviewCount';
import { MOBILE_TAB, MOBILE_TABS, type MobileTab } from './mobileTabs';

const svg = (children: ReactNode) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

// The four icons of the v4 phone canvas (`Phone.dc.html:105-108`), path for path.
const TAB_ICON = {
  now: svg(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>,
  ),
  jobs: svg(
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M7 9h10" />
      <path d="M7 13h10" />
    </>,
  ),
  inbox: svg(
    <>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </>,
  ),
  more: svg(
    <>
      <circle cx="5" cy="12" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
    </>,
  ),
} as const satisfies Record<MobileTab | 'more', ReactNode>;

const TAB_CLASS =
  'relative flex min-h-[52px] flex-col items-center gap-[3px] pt-2 pb-1 text-[11.5px] text-muted-foreground no-underline';
const TAB_ACTIVE_CLASS = 'text-foreground [&_svg]:text-primary';
const TABBAR_HEIGHT_VAR = '--app-tabbar-height';

/** Today's pending inbox count; mounted only on the Inbox tab (review:read). */
function InboxTabBadge() {
  const total = usePendingReviewCount().data?.total ?? 0;
  if (total <= 0) return null;
  return (
    <span className="absolute top-1 left-[calc(50%+6px)] rounded-full bg-warning px-[5px] text-[10px] leading-[15px] font-semibold text-warning-foreground">
      {total}
    </span>
  );
}

/**
 * The phone bottom bar (#2487 T1-T5): Now / Jobs / Inbox links filtered by
 * permission, lit from the path and the `_phone` view, and More, which opens
 * the sidebar drawer. In the layout's column after the content, never fixed.
 * Publishes its height as `--app-tabbar-height` on <body> while mounted, so
 * fixed overlays outside the layout (`PrivacyBanner`) sit above it; the height
 * includes the bottom safe area, hence measured, not a constant.
 */
export default function MobileTabBar({ onMore }: { onMore(): void }) {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const permissions = usePermissions();
  const tabs = MOBILE_TABS.filter((tab) => tab.permissions.every((p) => permissions.includes(p)));
  const navRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const style = document.body.style;
    const publish = () => style.setProperty(TABBAR_HEIGHT_VAR, `${nav.offsetHeight}px`);
    publish();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    observer?.observe(nav);
    return () => {
      observer?.disconnect();
      style.removeProperty(TABBAR_HEIGHT_VAR);
    };
  }, []);

  return (
    <nav
      ref={navRef}
      aria-label={t('mobileNav.label')}
      className="grid shrink-0 grid-cols-4 border-t border-border/50 bg-card pb-[max(18px,env(safe-area-inset-bottom))]"
    >
      {tabs.map((tab) => {
        const active = tab.isActive(pathname, search);
        return (
          <Link
            key={tab.key}
            to={tab.href}
            aria-current={active ? 'page' : undefined}
            className={active ? `${TAB_CLASS} ${TAB_ACTIVE_CLASS}` : TAB_CLASS}
          >
            {TAB_ICON[tab.key]}
            {t(tab.labelKey)}
            {tab.key === MOBILE_TAB.inbox && <InboxTabBadge />}
          </Link>
        );
      })}
      <button type="button" className={`${TAB_CLASS} cursor-pointer`} onClick={onMore}>
        {TAB_ICON.more}
        {t('mobileNav.more')}
      </button>
    </nav>
  );
}
